import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { SimulationFinancialsService } from '../../services/SimulationFinancials/SimulationFinancialsService';
import { SimulationFinancial } from '../../models/SimulationFinancials/SimulationFinancialsModels';
import { FinanceiraAuthChannel } from '../../models/SimulationBank/SimulationBankModels';

const ERROR_MESSAGES: Record<number, string> = {
    400: 'Dados inválidos, revise o formulário.',
    403: 'Permissão negada, seu partner_code não tem autorização.',
    404: 'Financeira não encontrada.',
};

interface ContextProps {
    financials: SimulationFinancial[];
    selected: SimulationFinancial | null;
    isLoading: boolean;
    isSaving: boolean;
    handleList: () => Promise<void>;
    handleSelect: (financial: SimulationFinancial) => void;
    handleUpdate: (isActive: boolean, authChannel?: FinanceiraAuthChannel | null) => Promise<boolean>;
}

export const SimulationFinancialsContext = createContext<ContextProps>({} as ContextProps);

export const SimulationFinancialsProvider = ({ children }: { children: ReactNode }) => {
    const [financials, setFinancials] = useState<SimulationFinancial[]>([]);
    const [selected, setSelected] = useState<SimulationFinancial | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const service = new SimulationFinancialsService();

    const handleList = useCallback(async () => {
        setIsLoading(true);
        const [_Response, _Error] = await service.list();

        if (_Error) {
            toast.error('Não foi possível carregar a lista de financeiras.');
            setFinancials([]);
            setIsLoading(false);
            return;
        }

        // GET simulation/screening/financials ainda não devolve description/products
        // (isso só chega com o módulo dedicado simulation_financials) — normaliza pro nosso
        // model mapeando is_active/canonical_id/updated_at/user_updated, e deixa o resto
        // como undefined por enquanto.
        const list: SimulationFinancial[] = (_Response?.data ?? []).map((item: any) => ({
            id: item.id,
            canonical_id: item.canonical_id,
            name: item.name,
            logo_url: item.logo_url,
            is_active: item.is_active,
            updated_at: item.updated_at,
            user_updated: item.user_updated,
            auth_channel: item.auth_channel ?? null,
        }));

        setFinancials(list);
        setIsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSelect = (financial: SimulationFinancial) => {
        setSelected(financial);
    };

    const handleUpdate = async (isActive: boolean, authChannel?: FinanceiraAuthChannel | null): Promise<boolean> => {
        if (!selected) return false;

        setIsSaving(true);
        const [_Response, _Error, _Code] = await service.update({
            id: selected.id,
            name: selected.name,
            canonical_id: selected.canonical_id,
            is_active: isActive,
            auth_channel: authChannel ?? undefined,
        });
        setIsSaving(false);

        if (_Error) {
            toast.error(ERROR_MESSAGES[_Code] ?? _Error ?? 'Um erro inesperado ocorreu, tente novamente mais tarde.');
            return false;
        }

        const item = _Response?.data ?? {};
        const updated: SimulationFinancial = {
            ...selected,
            is_active: item.is_active ?? isActive,
            updated_at: item.updated_at ?? selected.updated_at,
            user_updated: item.user_updated ?? selected.user_updated,
            auth_channel: item.auth_channel ?? authChannel ?? selected.auth_channel,
        };
        setFinancials((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
        setSelected(updated);
        toast.success('Financeira atualizada com sucesso.');
        return true;
    };

    useEffect(() => {
        handleList();
    }, [handleList]);

    return (
        <SimulationFinancialsContext.Provider
            value={{ financials, selected, isLoading, isSaving, handleList, handleSelect, handleUpdate }}
        >
            {children}
        </SimulationFinancialsContext.Provider>
    );
};

export const useSimulationFinancials = () => useContext(SimulationFinancialsContext);
